/**
 * Role and permission names, mirroring the backend's `services/permissions.js`.
 *
 * These are for rendering only — showing a link, hiding a button. The backend
 * re-checks every request against MongoDB, so a user who edits these values
 * in their browser changes what they see and nothing about what they can do.
 *
 * The permission *catalog* is deliberately not duplicated here: the admin
 * editor fetches it from `/api/admin/permissions/catalog`, so there is one
 * definition of what exists and the UI cannot drift from the enforcement.
 */

export const Role = Object.freeze({
  ADMIN: "admin",
  /**
   * Formerly `friend`. The backend resolves a stored `friend` to `marketing`
   * before it reaches the browser, so the old name never appears here.
   */
  MARKETING: "marketing",
  MEMBER: "member",
});

export const Permission = Object.freeze({
  PROFILE_READ: "profile:read",
  DASHBOARD_READ: "dashboard:read",
  GOLDBOT_READ: "goldbot:read",
  MY_TEAM_READ: "myteam:read",
  USERS_READ: "users:read",
  USERS_WRITE: "users:write",
  SUBSCRIBERS_READ: "subscribers:read",
  SUBSCRIBERS_WRITE: "subscribers:write",
});

export const UserStatus = Object.freeze({
  ACTIVE: "active",
  DISABLED: "disabled",
});

/** Human-readable role labels for the admin UI. */
export const ROLE_LABELS = Object.freeze({
  [Role.ADMIN]: "Admin",
  [Role.MARKETING]: "Marketing",
  [Role.MEMBER]: "Member",
});

export const ROLE_OPTIONS = Object.freeze([Role.ADMIN, Role.MARKETING, Role.MEMBER]);

/** What each role is for, shown beside the selector so the choice is informed. */
export const ROLE_DESCRIPTIONS = Object.freeze({
  [Role.ADMIN]: "Full access, including users, subscribers and permissions.",
  [Role.MARKETING]: "My Team only — the subscribers this person referred.",
  [Role.MEMBER]: "GoldBot dashboard access. No subscription needed — subscriptions are for the Telegram signals.",
});
